import { useState, useEffect, useCallback } from "react";
import { Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { AccountShell } from "../../components/account/AccountShell";
import { useAuth } from "../../hooks/useAuth";
import { getBillingInfo, updateBillingInfo } from "../../lib/api";

const FIELDS = [
  { key: "name", label: "Full name", placeholder: "Jane Founder" },
  { key: "email", label: "Billing email", placeholder: "you@example.com" },
  { key: "address_line1", label: "Address", placeholder: "123 Pine Tree Drive" },
  { key: "city", label: "City", placeholder: "Nassau" },
  { key: "state", label: "State / Province", placeholder: "N.P." },
  { key: "postal_code", label: "Postal code", placeholder: "00000" },
  { key: "country", label: "Country", placeholder: "Bahamas" },
];

export default function AccountSettings() {
  const { user, logout } = useAuth();
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!user) return;
    setLoading(true);
    getBillingInfo()
      .then((d) => setForm(d || {}))
      .catch(() => setForm({}))
      .finally(() => setLoading(false));
  }, [user]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    try {
      const saved = await updateBillingInfo(form);
      setForm(saved);
      toast.success("Billing information updated");
    } catch {
      toast.error("Could not update information.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AccountShell title="Account Settings" subtitle="Manage your account and billing information." testid="account-settings-page">
      {/* Account */}
      <div className="bg-white rounded-2xl p-7 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] mb-6" data-testid="account-info-card">
        <h2 className="font-display text-lg font-bold text-[#0F0F0F] mb-4">Account</h2>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 rounded-full bg-[#0F0F0F] text-[#D4AF37] flex items-center justify-center font-display text-xl font-bold overflow-hidden">
            {user?.picture ? (
              <img src={user.picture} alt={user.name || "Profile"} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
            ) : (
              "L"
            )}
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-[#0F0F0F] truncate">{user?.name || "Founder"}</p>
            <p className="text-sm text-[#687280] truncate">{user?.email}</p>
          </div>
        </div>
        <button
          onClick={logout}
          className="mt-5 inline-flex items-center gap-2 rounded-full border border-gray-200 px-5 py-2.5 text-sm font-semibold text-[#687280] hover:text-red-500 hover:border-red-200 transition-colors"
          data-testid="settings-signout-btn"
        >
          <LogOut size={15} /> Sign out
        </button>
      </div>

      {/* Billing Information */}
      <div className="bg-white rounded-2xl p-7 border border-gray-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid="billing-info-card">
        <h2 className="font-display text-lg font-bold text-[#0F0F0F] mb-1">Billing Information</h2>
        <p className="text-sm text-[#687280] mb-5">Used on your invoices and receipts.</p>
        {loading ? (
          <div className="flex items-center justify-center py-10 text-[#687280]">
            <Loader2 className="animate-spin" size={20} />
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {FIELDS.map((f) => (
                <div key={f.key} className={f.key === "address_line1" ? "sm:col-span-2" : ""}>
                  <label className="text-sm font-medium text-[#374151]">{f.label}</label>
                  <input
                    value={form[f.key] || ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    className="mt-1.5 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#D4AF37]"
                    data-testid={`billing-${f.key}-input`}
                  />
                </div>
              ))}
            </div>
            <button
              onClick={save}
              disabled={busy}
              className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-[#0F0F0F] text-white px-6 py-3 text-sm font-semibold hover:bg-[#1c1c1c] transition-colors disabled:opacity-60"
              data-testid="save-billing-btn"
            >
              {busy ? <Loader2 className="animate-spin" size={15} /> : "Update Information"}
            </button>
          </>
        )}
      </div>
    </AccountShell>
  );
}
